import { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import { PropInstanceVerticalDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { PropInstanceHandle, RoomLayoutInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { PropInstanceV1, PropInstanceV2 } from "building-types/props/propInstanceTypes";
import {
  defaultPropInstanceHorizontalAlignment,
  defaultPropInstanceLateralAlignment,
  defaultPropInstanceLateralDatum,
  defaultPropInstanceVerticalAlignment,
  defaultPropInstanceVerticalDatum,
} from "building-types/props/propInstanceTypes";
import type { Prop, PropAttributes } from "building-types/props/propTypes";
import { PlacementStyleVersion } from "building-types/props/propTypesEnum";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { PropViewNoteType } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { degreeToRadian } from "common/utils/math";
import uid from "common/utils/uid";
import { floorAssemblyTop } from "doc/elements/floor/floorAssembly";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { entityParamFromSpecificWallLayer } from "doc/elements/openings/openingWallLayersUtils";
import type { MergedPropLibrary } from "doc/propTools";
import { entityHandlesIfRoomLayoutInstanceOverride } from "doc/rooms/layout/roomLayoutTools";
import { mergedVerticalOffset, propAssetOffsetFromLine2 } from "doc/rooms/propPlacement";
import { defaultPropViewNotes } from "doc/settings/defaultViewNoteSettings";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { mergeViewNoteTables } from "doc/utils/viewNotesSettingsTools";
import { polyLine2Edge, polyLine2EdgeLength, polyLine2Reverse } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { isDeprecatedPropInstance } from "./blockUtils";
import { entityLateralOffsetFromWallDatum, getFromOrAddToWallLayerExtentsCache } from "./openings/openingWallLayers";

export async function generateProp({
  propAttributes,
  level,
  roomFloorAssembly,
  propInstanceId,
  propInstance,
  room,
  roomOutlines,
  roomIndex,
  wallIndex,
  mirror,
  wallMasses,
  handleOverride,
  wallLayerExtentsCache = {},
  mergedPropLibrary,
}: {
  propAttributes: PropAttributes;
  level: Level;
  roomFloorAssembly: FloorAssembly;
  propInstanceId: string;
  propInstance: PropInstanceV2 | PropInstanceV1;
  room: Room;
  roomOutlines: RoomOutlines;
  handleOverride?: RoomLayoutInstanceHandle;
  roomIndex: RoomIndex;
  wallIndex: WallIndex;
  mirror: boolean;
  wallMasses: Table<WallMassSystem>;
  wallLayerExtentsCache?: WallLayerExtentsCache;
  mergedPropLibrary: MergedPropLibrary;
}): Promise<Prop | null> {
  const {
    propType,
    edgeId,
    horizontalAlignment = defaultPropInstanceHorizontalAlignment,
    lateralDatum = defaultPropInstanceLateralDatum,
    lateralAlignment = defaultPropInstanceLateralAlignment,
    lateralOffset = 0,
    lateralOffsetNormalized = false,
    verticalDatum = defaultPropInstanceVerticalDatum,
    verticalAlignment = defaultPropInstanceVerticalAlignment,
    verticalOffset,
    rotation: propRotation,
    mirrorPropX,
    mirrorPropY,
    useWallStripMaxOffset,
    labels: propInstanceLabels,
    placementStyle = PlacementStyleVersion.DEPRECATED,
    floorMaterialOverride,
    catalogType,
    materialOverrides,
    useExteriorPalette,
    entityInstanceType,
    floorPlanTag,
    floorPlanTagOffsetX,
    floorPlanTagOffsetY,
    isExterior,
  } = propInstance;

  const edgeIndex = room.edgeIds.indexOf(edgeId);

  if (!propAttributes) {
    console.warn(`propAttributes is undefined for propInstanceId=${propInstanceId}`);
    return null;
  }

  if (edgeIndex === -1) {
    console.warn(`edgeId is invalid for roomId=${room.id}`);
    return null;
  }

  const { handle, handleAuxiliary } = entityHandlesIfRoomLayoutInstanceOverride(
    { type: "PROP_INSTANCE", id: propInstanceId } as PropInstanceHandle,
    handleOverride,
  );

  const resolvedLabels = { ...propAttributes.labels, ...propInstanceLabels };

  const propViewNotes = mergeViewNoteTables(defaultPropViewNotes, propAttributes.viewNotes, propInstance?.viewNotes);

  const defaultVerticalOffset = propAttributes.defaultVerticalOffset ?? 0;
  const resolvedVerticalOffset = mergedVerticalOffset(verticalOffset, defaultVerticalOffset);

  const propViewEntityDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.PROP,
    propViewNotes[PropViewNoteType.PROP],
  );

  const z = verticalOffsetFromDatumAdjustment(level, roomFloorAssembly, room, verticalDatum) + resolvedVerticalOffset;
  const wallStrip = wallIndex.wallStripFromRoomEdgeIndex(room, edgeIndex);

  if (!wallStrip) {
    return null;
  }

  const baseLineMaxOffsetEdge = wallStrip.baselineMaxOffset(true);

  let resolvedParam = propInstance.param;
  let resolvedLateralOffset = lateralOffset;
  let resolvedPropRotation = propRotation; // in degrees
  let edge: PolyLine2;
  const isDeprecatedProp = isDeprecatedPropInstance(propInstance);

  if (isDeprecatedProp) {
    edge = useWallStripMaxOffset ? baseLineMaxOffsetEdge : wallStrip.baseline(true);

    if (mirror) {
      polyLine2Reverse(edge);
      resolvedLateralOffset = -resolvedLateralOffset;
      resolvedPropRotation += 180;
    }

    const lineLength = polyLine2EdgeLength(edge, 0);
    if (propInstance.paramNormalized) {
      resolvedParam = lineLength * resolvedParam;
    }
  } else {
    const wallMassSystem = wallMasses[room.level.id];

    edge = polyLine2Edge(room.outline, edgeIndex);
    if (!edge) {
      return null;
    }

    const { edgeMinTable, edgeMaxTable, largestInsideOffset, mostInwardLayerIntoRoom, roomEdgeLength } =
      getFromOrAddToWallLayerExtentsCache(room, edgeIndex, wallLayerExtentsCache, roomIndex, wallMassSystem);

    const { param } = entityParamFromSpecificWallLayer({
      placement: propInstance,
      largestOffset: largestInsideOffset,
      edgeLength: roomEdgeLength,
      mirror,
      mostInwardLayerIntoRoom,
      edgeMinTable,
      edgeMaxTable,
    });
    resolvedParam = param;

    resolvedLateralOffset = entityLateralOffsetFromWallDatum({
      room,
      edgeIndex,
      roomOutlines,
      wallMassSystem,
      placement: {
        lateralDatum: useWallStripMaxOffset ? WallLateralDatumAnchorAll.WALL_OFFSET : lateralDatum,
        lateralOffsetNormalized: lateralOffsetNormalized,
        lateralOffset,
      },
      resolvedParam,
    });
  }

  const assetId = propAttributes.propAssetId;
  const propAsset = await mergedPropLibrary.get(assetId);
  if (!propAsset) {
    return null;
  }
  const { scaleX, scaleY, scaleZ, uniformScale } = propAttributes;

  return propAssetOffsetFromLine2({
    room,
    edge,
    propAsset,
    propId: propType,
    param: resolvedParam,
    paramNormalized: false,
    paramAnchor: propInstance.paramAnchor,
    horizontalAlignment,
    verticalAlignment,
    lateralDatum,
    lateralAlignment,
    rotate: degreeToRadian(resolvedPropRotation) * (mirror ? -1 : 1),
    offset: resolvedLateralOffset,
    z,
    placementStyle,
    handle,
    handleAuxiliary,
    useWallStripMaxOffset: !!useWallStripMaxOffset,
    mirror,
    mirrorPropX,
    mirrorPropY,
    uniformScale,
    scaleX,
    scaleY,
    scaleZ,
    labels: resolvedLabels,
    floorMaterial: floorMaterialOverride ?? propAttributes.floorMaterialOverride,
    descriptor: propViewEntityDescriptor,
    catalogType,
    materialOverrides,
    useExteriorPalette,
    isDeprecatedWallProp: isDeprecatedProp,
    entityInstanceType,
    floorPlanTag,
    floorPlanTagOffsetX: floorPlanTagOffsetX !== undefined && mirror ? -floorPlanTagOffsetX : floorPlanTagOffsetX,
    floorPlanTagOffsetY,
    isExterior,
  });
}

function verticalOffsetFromDatumAdjustment(
  level: Level,
  floor: FloorAssembly,
  room: Room,
  verticalDatum: PropInstanceVerticalDatumAnchor,
) {
  switch (verticalDatum) {
    case PropInstanceVerticalDatumAnchor.LEVEL_FLOOR_ELEVATION:
      return level.floorElevation;
    case PropInstanceVerticalDatumAnchor.LEVEL_GRADE:
      return level.floorElevation + level.levelDatumsGenerated.grade;
    case PropInstanceVerticalDatumAnchor.ROOM_FLOOR_ELEVATION:
      return floorAssemblyTop(floor, room.level);
    default:
      assertUnreachable(verticalDatum);
  }
}
